import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { LayoutGrid, Network, List } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAgents } from '@/hooks/useAgents'
import type { AgentSummary } from '@/lib/api'
import {
  COUNCIL_BRANCH,
  GUARDIAN_BRANCH,
  getPhaseLabel,
  type AgentPhase,
  type OrgAgent,
  type OrgBranch,
  type OrgGroup,
} from '@/lib/agent-org'
import { getAgentIcon } from '@/components/agents/AgentIcons'
import { AgentStatusGrid } from '@/components/agents/AgentStatusGrid'
import { IntegrationHealthGrid } from '@/components/agents/IntegrationHealthGrid'
import { KnowledgeBasePanel } from '@/components/agents/KnowledgeBasePanel'
import { AgentDirectory } from '@/components/agents/AgentDirectory'

type AgentsView = 'directory' | 'grid' | 'org'
const VIEW_KEY = 'tmagent:agents-view'

const VIEW_META: Record<AgentsView, { title: string; blurb: string }> = {
  directory: {
    title: 'Agent Directory',
    blurb: 'Every agent across both repos — click any row for schedule, data flow, and live status.',
  },
  grid: {
    title: 'Status Board',
    blurb: 'Every live agent and service at a glance — green, amber, red.',
  },
  org: {
    title: 'Org Chart',
    blurb: 'Council (Zeus, CEO) and Guardians (Athena, COO) — peers under Mark.',
  },
}

// ─── Time + status helpers ───────────────────────────────────────────────────

function timeAgo(iso: string | null): string {
  if (!iso) return 'NEVER'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'JUST NOW'
  if (mins < 60) return `${mins}M AGO`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}H AGO`
  return `${Math.floor(hours / 24)}D AGO`
}

function statusRailColor(phase: AgentPhase, live: AgentSummary | undefined): string {
  if (phase === 'phase-2' || phase === 'phase-3-4') return 'bg-cmd-outline-variant'
  if (phase === 'infrastructure' && !live) return 'bg-[#22d3ee]/40'
  if (!live) return 'bg-cmd-outline-variant'
  switch (live.status) {
    case 'active': return 'bg-primary'
    case 'paused': return 'bg-[#ffd080]'
    case 'error':  return 'bg-cmd-error'
    default:       return 'bg-cmd-outline-variant'
  }
}

// ─── Mark header ─────────────────────────────────────────────────────────────

function MarkHeader() {
  return (
    <div className="bg-cmd-low border-l-2 border-white/40 px-5 py-3">
      <p className="font-label text-[0.6rem] tracking-widest text-cmd-outline uppercase">
        Founder
      </p>
      <div className="mt-0.5 flex items-baseline gap-3">
        <h1 className="font-sans text-lg font-semibold text-white tracking-tight">
          Mark Livsey
        </h1>
        <span className="font-mono text-[0.65rem] text-cmd-outline">
          DEC-021 · Two-briefing structure · Two decision queues
        </span>
      </div>
    </div>
  )
}

// ─── Executive header (Zeus / Athena) ────────────────────────────────────────

function ExecutiveHeader({ branch }: { branch: OrgBranch }) {
  const liveCount = branch.groups
    .filter((g) => g.phase === 'live' || g.phase === 'infrastructure')
    .reduce((sum, g) => sum + g.agents.length, 0)

  return (
    <div className="relative bg-cmd-container">
      <div
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ backgroundColor: branch.accent }}
      />
      <div className="px-5 py-4 pl-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p
              className="font-label text-[0.6rem] tracking-widest uppercase"
              style={{ color: branch.accent }}
            >
              {branch.branch === 'council' ? 'Council' : 'Guardians'} · {branch.runtime}
            </p>
            <h2 className="mt-1 font-sans text-2xl font-semibold text-white tracking-tight">
              {branch.executive}
            </h2>
            <p className="font-label text-[0.7rem] text-white/70 mt-0.5">
              {branch.title}
            </p>
            <p className="font-label text-[0.65rem] text-cmd-outline mt-2 leading-relaxed">
              {branch.blurb}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="font-label text-[0.6rem] tracking-widest text-cmd-outline uppercase">
              Agents
            </p>
            <p className="font-mono text-2xl font-medium text-white tabular-nums leading-none mt-1">
              {liveCount}
              <span className="text-cmd-outline text-base">/{branch.totalAgents}</span>
            </p>
            <p className="font-label text-[0.55rem] tracking-widest text-cmd-outline uppercase mt-1">
              Live / Planned
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Agent card ──────────────────────────────────────────────────────────────

function AgentCard({ agent, live }: { agent: OrgAgent; live: AgentSummary | undefined }) {
  const isPlanned = agent.phase === 'phase-2' || agent.phase === 'phase-3-4'
  const isInfraOnly = agent.phase === 'infrastructure' && !live
  const dim = isPlanned || isInfraOnly

  const Icon = getAgentIcon(agent.skillName) ?? getAgentIcon(agent.displayName)

  return (
    <div className={`relative bg-cmd-container ${dim ? 'opacity-55' : ''}`}>
      <div
        className="absolute left-0 top-0 bottom-0 w-0.5"
        style={
          dim
            ? { backgroundColor: '#3a3f47' }
            : { backgroundColor: agent.accent }
        }
      />
      <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${statusRailColor(agent.phase, live)}`} style={{ opacity: dim ? 0 : 1 }} />

      <div className="p-3.5 pl-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center bg-cmd-high text-base">
            {Icon ? <Icon size={32} /> : <span>{agent.emoji}</span>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-sans text-sm font-semibold text-white truncate">
                {agent.displayName}
              </h3>
              <Badge
                variant={agent.phase === 'live' ? 'success' : agent.phase === 'infrastructure' ? 'secondary' : 'outline'}
                className="text-[9px] px-1 py-0 shrink-0 font-mono"
              >
                {getPhaseLabel(agent.phase)}
              </Badge>
            </div>
            <p className="font-label text-[0.6rem] text-cmd-outline truncate uppercase mt-0.5">
              {agent.role}
            </p>
          </div>
        </div>

        {/* Body: live data or planned placeholder */}
        {live ? (
          <>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <CardStat label="Runs (7d)" value={`${live.runs_today} (${live.runs_7d})`} />
              <CardStat
                label="Success"
                value={live.success_rate_24h !== null ? `${live.success_rate_24h}%` : '--'}
              />
              <CardStat label="Last" value={timeAgo(live.last_run_at)} />
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className="font-mono text-[0.6rem] text-cmd-outline-variant truncate">
                {live.schedule_summary ?? 'Event-driven'}
              </span>
              {live.pending_decisions > 0 && (
                <Link
                  to={`/dashboard/decisions?agent=${encodeURIComponent(live.name)}`}
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0"
                >
                  <Badge variant="high" className="text-[9px] px-1 py-0 hover:underline">
                    {live.pending_decisions} WAITING
                  </Badge>
                </Link>
              )}
            </div>
          </>
        ) : (
          <div className="mt-3 flex items-center gap-2">
            <span className="font-mono text-[0.6rem] text-cmd-outline-variant uppercase">
              {agent.phase === 'phase-2'
                ? 'Awaiting build'
                : agent.phase === 'phase-3-4'
                ? 'Phase 3–4 backlog'
                : 'No runtime telemetry'}
            </span>
          </div>
        )}

        <div className="mt-2.5 flex items-center justify-between">
          <span className="font-mono text-[0.6rem] text-cmd-outline-variant">
            {agent.model}
          </span>
          {live?.disabled_by_env && (
            <Badge variant="secondary" className="text-[9px] px-1 py-0" title="Listed in DISABLED_CRON_SKILLS — scheduled runs are skipped">
              CRON OFF
            </Badge>
          )}
          {live?.last_run_outcome && (
            <Badge
              variant={live.last_run_outcome === 'success' ? 'success' : 'destructive'}
              className="text-[9px] px-1 py-0"
            >
              {live.last_run_outcome.toUpperCase()}
            </Badge>
          )}
        </div>
      </div>
    </div>
  )
}

function CardStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-label text-[0.55rem] tracking-widest text-cmd-outline uppercase">
        {label}
      </p>
      <p className="font-mono text-xs font-medium text-white tabular-nums mt-0.5">
        {value}
      </p>
    </div>
  )
}

// ─── Group section ───────────────────────────────────────────────────────────

function GroupSection({
  group,
  liveByName,
}: {
  group: OrgGroup
  liveByName: Map<string, AgentSummary>
}) {
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <div>
          <h3 className="font-sans text-[0.7rem] font-semibold tracking-widest text-white uppercase">
            {group.label}
          </h3>
          <p className="font-label text-[0.6rem] text-cmd-outline">{group.blurb}</p>
        </div>
        <span className="font-mono text-[0.6rem] text-cmd-outline-variant tabular-nums">
          {group.agents.length}
        </span>
      </div>
      <div className="grid gap-1 sm:grid-cols-2">
        {group.agents.map((agent) => (
          <AgentCard
            key={agent.skillName}
            agent={agent}
            live={liveByName.get(agent.skillName)}
          />
        ))}
      </div>
    </div>
  )
}

// ─── Branch column (Zeus or Athena) ──────────────────────────────────────────

function BranchColumn({
  branch,
  liveByName,
}: {
  branch: OrgBranch
  liveByName: Map<string, AgentSummary>
}) {
  return (
    <div className="flex flex-col gap-4">
      <ExecutiveHeader branch={branch} />
      <div className="flex flex-col gap-5">
        {branch.groups.map((group) => (
          <GroupSection key={group.key} group={group} liveByName={liveByName} />
        ))}
      </div>
    </div>
  )
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function AgentSkeletonCard() {
  return (
    <div className="bg-cmd-container p-3.5 space-y-3">
      <div className="flex items-center gap-3">
        <Skeleton className="h-9 w-9" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-2.5 w-16" />
        </div>
      </div>
      <Skeleton className="h-7 w-full" />
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {[0, 1].map((col) => (
        <div key={col} className="flex flex-col gap-4">
          <Skeleton className="h-24 w-full" />
          <div className="grid gap-1 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <AgentSkeletonCard key={i} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function AgentCouncil() {
  const { data, isLoading, error } = useAgents()
  const agents: AgentSummary[] = data?.data ?? []

  const [view, setView] = useState<AgentsView>(() => {
    if (typeof localStorage === 'undefined') return 'directory'
    const saved = localStorage.getItem(VIEW_KEY)
    return saved === 'org' || saved === 'grid' ? saved : 'directory'
  })
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view)
    } catch {
      /* storage unavailable — keep the choice in memory only */
    }
  }, [view])

  const liveByName = new Map<string, AgentSummary>()
  for (const a of agents) {
    liveByName.set(a.name, a)
  }
  // Legacy alias: Athena rows were once written as 'chief-of-staff'.
  if (!liveByName.has('guardian-coo') && liveByName.has('chief-of-staff')) {
    liveByName.set('guardian-coo', liveByName.get('chief-of-staff')!)
  }

  return (
    <div className="space-y-6">
      {/* Page title + view toggle */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-sans text-xl font-semibold text-white tracking-tight">
            {VIEW_META[view].title}
          </h1>
          <p className="font-label text-xs text-cmd-outline mt-1">{VIEW_META[view].blurb}</p>
        </div>
        <div className="flex items-center gap-1 bg-cmd-container p-1" role="group" aria-label="Agents view">
          <Button
            size="sm"
            variant={view === 'directory' ? 'default' : 'ghost'}
            onClick={() => setView('directory')}
            aria-pressed={view === 'directory'}
            className="font-label text-[0.65rem] tracking-wider"
          >
            <List className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            DIRECTORY
          </Button>
          <Button
            size="sm"
            variant={view === 'grid' ? 'default' : 'ghost'}
            onClick={() => setView('grid')}
            aria-pressed={view === 'grid'}
            className="font-label text-[0.65rem] tracking-wider"
          >
            <LayoutGrid className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            STATUS GRID
          </Button>
          <Button
            size="sm"
            variant={view === 'org' ? 'default' : 'ghost'}
            onClick={() => setView('org')}
            aria-pressed={view === 'org'}
            className="font-label text-[0.65rem] tracking-wider"
          >
            <Network className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            ORG CHART
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-cmd-error-container p-4">
          <p className="text-sm text-cmd-on-error font-label">
            FAULT DETECTED: {error.message}
          </p>
        </div>
      )}

      {view === 'directory' ? (
        <AgentDirectory />
      ) : view === 'grid' ? (
        <AgentStatusGrid agents={agents} isLoading={isLoading} />
      ) : (
        <>
          <MarkHeader />
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              <BranchColumn branch={COUNCIL_BRANCH} liveByName={liveByName} />
              <BranchColumn branch={GUARDIAN_BRANCH} liveByName={liveByName} />
            </div>
          )}
        </>
      )}

      {/* Hecate — Integration Hub (TMA-101). Lives on the Agents page, not a
          standalone route. */}
      <IntegrationHealthGrid />

      {/* Hestia — Knowledge Base (TMA-93). Lives on the Agents page, not a
          standalone route. */}
      <KnowledgeBasePanel hestia={liveByName.get('knowledge-base')} />
    </div>
  )
}
